import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import "./Projects.css";

import hexapod from "../assets/img/Projects/MORF/hexapod.png";
import morfRoute from "../assets/img/Projects/MORF/Morf_experiment_route.jpg";
import morfPaper from "../assets/img/Projects/MORF/ResearchPaper_front_page.png";
import dndUsage from "../assets/img/Projects/DnD-Screenplay/DnD-Screenplay-usage.png";
import dndScreen from "../assets/img/Projects/DnD-Screenplay/TV-Screen.jpg";
import dndSplash from "../assets/img/Projects/DnD-Screenplay/DnD-Screenplay_Splash.png";
import iotPuzzle from "../assets/img/Projects/Escape room Plan it B/Puzzlebox-implementatie.jpg";
import iotUvPuzzle from "../assets/img/Projects/Escape room Plan it B/Uv-puzzel-Realisatie.jpg";
import iotUvSlot from "../assets/img/Projects/Escape room Plan it B/Uv-slot-implementatie.jpg";
import iotDocs from "../assets/img/Projects/Escape room Plan it B/Documentatie-implementatie.jpg";
import iotLogo from "../assets/img/Projects/Escape room Plan it B/planit B2-modified.jpeg";
import droneVideo from "../assets/img/Projects/DroneFreestyling/40 seconds fun.mp4";
import soccerDiagram   from "../assets/img/Projects/Soccer team detector/CV_diagram.png";
import soccerSegment   from "../assets/img/Projects/Soccer team detector/Merged_Segmentation.png";
import soccerClusters  from "../assets/img/Projects/Soccer team detector/3mediod_clusters_full_inferenence.png";
import soccerGk        from "../assets/img/Projects/Soccer team detector/GK_manual_picker_histogram_plot.png";
import soccerGif       from "../assets/img/Projects/Soccer team detector/soccer_detection.gif";

const EXTERNAL_LINK_ICON = (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M6 3.5A.5.5 0 0 1 6.5 3h6a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V4.707L6.354 10.854a.5.5 0 1 1-.708-.708L11.793 4H6.5a.5.5 0 0 1-.5-.5z" />
    <path d="M3 5.5A1.5 1.5 0 0 1 4.5 4h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 1 1 0v2a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 12.5v-7z" />
  </svg>
);

const PROJECTS = [
  {
    id: "drone-freestyle",
    title: "FPV Drone Freestyling",
    subtitle: "First electronics workbench, quads built, soldered and tuned by hand",
    year: "2017 — 2020",
    role: "Solo build · pilot · YouTube channel @Spring-B FPV",
    link: "https://www.youtube.com/@sprint-bfpv4598",
    linkLabel: "Watch on YouTube",
    body: "Long before research papers and ROS nodes, there was a soldering iron and a pile of carbon-fibre frames. I built every quad from the bare frame up; soldering the flight controller to the ESCs, ESCs to motors, wiring the FPV camera and video transmitter, binding receivers and tuning the radio. PID loops were dialled in by feel: rip a few packs, crash, tweak P, I, D, repeat until it flew the way I wanted. Full FPV setup, real first-person flight. It taught me that a system you've built end-to-end is the only kind you ever fully understand.",
    stack: ["Soldering", "Flight controllers", "ESCs & motors", "FPV camera + VTX", "Receiver / transmitter", "PID tuning", "Betaflight"],
    kind: "video",
    badge: "Where it started",
    tags: ["Drones"],
    videoSrc: droneVideo,
  },
  {
    id: "iot-escape-room",
    title: "IoT Escape Room — Plan-it B",
    subtitle: "Sustainable Development Goals themed escape room",
    year: "2022",
    role: "Technical lead for the cummunication framework · Solo puzzle dev",
    link: "https://plan-it-b.github.io/ba3-docs/",
    linkLabel: "Read the documentation",
    body: "My responsibilities went beyond a single puzzle. As technical lead for the overall communication framework, I designed and implemented the MQTT-based messaging architecture that connected every puzzle in the room and let them exchange state information in real time. I also independently conceived, built and delivered the final puzzle of the room. Integrating hardware, software and game logic from scratch.",
    stack: ["IoT", "C++", "ESP32", "MQTT", "Embedded systems", "PCB design", "Electronics", "Technical leadership"],
    kind: "gallery",
    badge: "IoT · Technical lead",
    tags: ["IoT"],
    gallery: [
      { src: iotPuzzle,   caption: "Central puzzle box locking players phones in a box — keypad, solanoid, LCD prompt and 60-minute countdown timer. Designed and built solo." },
      { src: iotUvPuzzle, caption: "Solved UV-light puzzle - parts were hidden across the room." },
      { src: iotUvSlot,   caption: "SDG-themed final puzzle — Designed and built solo." },
      { src: iotDocs,     caption: "Players testing puzzles." },
      { src: iotLogo,     caption: "Plan-it B — the team behind the room.", bg: "#ffffff" },
    ],
  },
  {
    id: "hexapod",
    title: "Hexapod Robot with Soft Skin",
    subtitle: "Master's thesis · SDU Odense · Published research",
    year: "2022 — 2023",
    role: "Researcher & developer",
    link: "https://www.liebertpub.com/doi/10.1089/rorep.2024.0035",
    linkLabel: "Read the paper",
    body: "A bio-inspired hexapod fitted with a pneumatic soft-skin sleeve and synchronised LEDs. ROS nodes tie the breathing rhythm and lighting to movement state; standstill, moving forward, turning, stopping. Evaluated in a controlled user study: informative light cues improved perceived appropriateness, while biomorphic breathing increased discomfort.",
    stack: ["ROS", "C++", "Python", "Arduino", "Embedded Linux", "Pneumatics", "Research", "Innovation",],
    kind: "gallery",
    badge: "Published research",
    tags: ["Robotics"],
    gallery: [
      { src: hexapod,   caption: "The hexapod with its pneumatic soft-skin sleeve — lights off vs on + configurations." },
      { src: morfRoute, caption: "User-study experiment" },
      { src: morfPaper, caption: "Published in Robotics Reports (Liebert Open Access), 2024." },
    ],
  },
  {
    id: "daikin",
    title: "Heat-Pump Diagnostics Tool",
    subtitle: "Daikin Europe · flagship logging platform",
    year: "2023 — present",
    role: "Project lead · full lifecycle ownership",
    body: "A feature-rich logging & diagnostics application for Daikin's newest generation of heat pumps, serving field quality engineers and on-site installers alike. Owned the full lifecycle: requirements gathering, specifications, supplier alignment, and structured validation plans; from functional verification to real-world field testing.",
    stack: ["Python", "PyQT", "Project management", "Requirements", "Acceptance testing", "Agile"],
    kind: "text",
    tags: ["Software"],
  },
  {
    id: "portfolio",
    title: "Portfolio Website",
    subtitle: "vandeputte-software-development.com",
    year: "2024 — present",
    role: "Full-stack · CI/CD via Firebase",
    body: "This site, end-to-end, from design to development and automated deployment. Getting a feel for webdevelopment and experiment with React, while building a personal playground for my projects and thoughts.",
    stack: ["React", "Node.js", "Bootstrap 5", "Firebase", "Javascript", "CSS", "HTML"],
    kind: "text",
    tags: ["Software"],
  },
  {
    id: "soccer-team-detector",
    title: "Soccer Team Detector",
    subtitle: "Real-time player detection and team assignment from broadcast football footage",
    year: "2026",
    role: "Solo · Computer vision engineer",
    body: "An end-to-end CV pipeline that detects players, segments them per-pixel, and assigns team colours in a single YOLOv26-seg forward pass. Each jersey is encoded as a special hue-saturation-value histogram over a mask-gated torso crop, eliminating background bleed without handcrafted heuristics and robutsness agains different lighting conditions. Team assignment is solved unsupervised via k-medoids clustering with Bhattacharyya distance, fitted on a warm-up window then frozen. Goalkeepers are handled through tool for goalkeeper bouding box selection and team assignment, then the selected histogram gets extracted and used as centroid for clustering algorithm. The project outputs annotated video with live team-count HUD, collor assignment based on predicted team class and ball segmenation.",
    stack: ["Python", "PyTorch", "Ultralytics YOLO", "OpenCV", "scikit-learn"],
    kind: "gallery",
    badge: "Computer Vision",
    tags: ["AI"],
    gallery: [
      { src: soccerDiagram,  caption: "End-to-end pipeline: YOLO-seg → histogram encoding → k-medoids team clustering." },
      { src: soccerSegment,  caption: "Instance segmentation output — per-pixel body masks isolate each player from the background." },
      { src: soccerClusters, caption: "K-medoids clustering with Bhattacharyya distance assigns players to two teams unsupervised." },
      { src: soccerGk,       caption: "Goalkeeper colour anchor picker — RGB reference histograms injected at inference time." },
      { src: soccerGif,      caption: "Live annotated output — team-count HUD, bounding boxes and team colours at ~10–20 fps." },
    ],
  },
  {
    id: "dnd-screenplay",
    title: "DnD-Screenplay",
    subtitle: "Desktop app for tabletop RPG game masters — with built-in computer vision",
    year: "2026 — present",
    role: "Solo dev · Full stack · Custom CV pipeline",
    body: "A PySide6 desktop app that projects dynamic battle maps onto a second screen at exact physical scale. Real-time fog of war with line-of-sight ray-casting, drag-and-drop tokens, session persistence, packaged as a standalone Windows executable. Includes a multi-task computer vision system that analyses uploaded battle maps in a single forward pass. Predicting lighting, grid scale and pixel-level wall segmentation for fog of war calulcations. Three encoders benchmarked: EfficientNet-B0, SegFormer (ViT) and Meta's Segment Anything Model, sharing a U-Net decoder. Exported to ONNX for lightweight inference.",
    stack: ["Python", "PySide6", "PyTorch", "OpenCV", "SAM", "ONNX", "pytest"],
    kind: "gallery",
    badge: "Software + AI",
    tags: ["Software", "AI"],
    gallery: [
      { src: dndUsage, caption: "Both windows in play — DM admin panel right, player view projection on left." },
      { src: dndScreen, caption: "Custom-built TV in wooden frame — running the player view at 1:1 physical scale." },
      { src: dndSplash, caption: "DnD-Screenplay — splash screen.", scale: 0.5 },
    ],
  },
];

const FILTERS = [
  { id: "all",         label: "All" },
  { id: "AI",       label: "AI" },
  { id: "Robotics", label: "Robotics" },
  { id: "Drones",      label: "Drones" },
  { id: "Software",    label: "Software" },
  { id: "IoT",         label: "IoT" },
];

function TechChip({ label }) {
  return <span className="vsd-chip">{label}</span>;
}

function CardMeta({ year, role }) {
  return (
    <div className="vsd-pc__meta">
      <span className="vsd-pc__year">{year}</span>
      {role && (
        <>
          <span className="vsd-pc__dot" />
          <span className="vsd-pc__role">{role}</span>
        </>
      )}
    </div>
  );
}

function CardBody({ p, index }) {
  return (
    <>
      <div className="vsd-pc__body-top">
        <div className="vsd-pc__index">{String(index + 1).padStart(2, "0")}</div>
        <CardMeta year={p.year} role={p.role} />
        <h3 className="vsd-pc__title">{p.title}</h3>
        <p className="vsd-pc__subtitle">{p.subtitle}</p>
      </div>
      <div className="vsd-pc__body-bot">
        <p className="vsd-pc__desc">{p.body}</p>
        <div className="vsd-pc__stack">
          {p.stack.map((s) => <TechChip key={s} label={s} />)}
        </div>
        {p.link && (
          <a className="vsd-pc__link" href={p.link} target="_blank" rel="noreferrer">
            {p.linkLabel}
            {EXTERNAL_LINK_ICON}
          </a>
        )}
      </div>
    </>
  );
}

const isVideo = (src) => /\.(mp4|webm|mov)$/i.test(src);

function Lightbox({ gallery, index, onClose, onChange }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && index > 0) onChange(index - 1);
      if (e.key === "ArrowRight" && index < gallery.length - 1) onChange(index + 1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [index, gallery.length, onClose, onChange]);

  const cur = gallery[index];

  return createPortal(
    <div
      className="vsd-lightbox"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
    >
      <div className="vsd-lightbox__inner" onClick={(e) => e.stopPropagation()}>
        {isVideo(cur.src)
          ? <video src={cur.src} controls style={{ maxWidth: "100%", maxHeight: "70vh" }} />
          : <img src={cur.src} alt={cur.caption} />}
        {cur.caption && <p className="vsd-lightbox__caption">{cur.caption}</p>}
        {gallery.length > 1 && (
          <div className="vsd-lightbox__nav">
            <button
              className="vsd-lightbox__arrow"
              disabled={index === 0}
              onClick={() => onChange(index - 1)}
              aria-label="Previous image"
            >
              ←
            </button>
            <span className="vsd-lightbox__counter">{index + 1} / {gallery.length}</span>
            <button
              className="vsd-lightbox__arrow"
              disabled={index === gallery.length - 1}
              onClick={() => onChange(index + 1)}
              aria-label="Next image"
            >
              →
            </button>
          </div>
        )}
      </div>
      <button className="vsd-lightbox__close" onClick={onClose} aria-label="Close">✕</button>
    </div>,
    document.body
  );
}

function GalleryCard({ p, index }) {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const cur = p.gallery[active];

  return (
    <article className="vsd-pc vsd-pc--gallery" id={`project-${p.id}`}>
      <div className="vsd-pc__media vsd-pc__media--gallery">
        {p.badge && <span className="vsd-pc__badge">{p.badge}</span>}
        <div
          className="vsd-gal__stage"
          style={{ ...(cur.bg ? { background: cur.bg } : {}), ...(isVideo(cur.src) ? { cursor: "default" } : {}) }}
          onClick={() => { if (!isVideo(cur.src)) setLightbox(active); }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setLightbox(active)}
          aria-label="Open full screen"
        >
          {isVideo(cur.src)
            ? <video key={cur.src} src={cur.src} controls playsInline preload="metadata" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
            : <img key={cur.src} src={cur.src} alt={cur.caption} style={cur.scale ? { "--img-scale": cur.scale } : undefined} />}
        </div>
        <p className="vsd-gal__caption">{cur.caption}</p>
        <div className="vsd-gal__thumbs">
          {p.gallery.map((g, i) => (
            <button
              key={g.src}
              className={`vsd-gal__thumb${i === active ? " is-active" : ""}`}
              onClick={() => setActive(i)}
              aria-label={`Show image ${i + 1}`}
              style={g.bg ? { background: g.bg } : undefined}
            >
              {isVideo(g.src)
                ? <video src={g.src} muted playsInline preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }} />
                : <img src={g.src} alt="" style={g.scale ? { transform: `scale(${g.scale})` } : undefined} />}
            </button>
          ))}
        </div>
      </div>
      <div className="vsd-pc__body">
        <CardBody p={p} index={index} />
      </div>
      {lightbox !== null && (
        <Lightbox
          gallery={p.gallery}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onChange={setLightbox}
        />
      )}
    </article>
  );
}

function VideoCard({ p, index }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <article className="vsd-pc vsd-pc--video" id={`project-${p.id}`}>
      <div className="vsd-pc__media vsd-pc__media--video">
        <div className="vsd-vid__frame">
          <div className="vsd-vid__screen">
            <video
              ref={videoRef}
              src={p.videoSrc}
              muted
              loop
              playsInline
              preload="auto"
            />
            <div className="vsd-vid__hud" aria-hidden="true">
              <div className="vsd-vid__hud-row">
                <span className="vsd-vid__hud-pill is-live"><span /> REC</span>
                <span className="vsd-vid__hud-pill">5.8 GHz</span>
              </div>
              <div className="vsd-vid__hud-row">
                <span className="vsd-vid__hud-pill">14.8 V</span>
                <span className="vsd-vid__hud-pill">RSSI 92%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {p.badge && <span className="vsd-pc__badge">{p.badge}</span>}
      <div className="vsd-pc__body">
        <CardBody p={p} index={index} />
      </div>
    </article>
  );
}

function TextCard({ p, index }) {
  return (
    <article className="vsd-pc vsd-pc--text" id={`project-${p.id}`}>
      <div className="vsd-pc__body">
        <CardBody p={p} index={index} />
      </div>
    </article>
  );
}

function ProjectCard({ p, index }) {
  if (p.kind === "gallery") return <GalleryCard p={p} index={index} />;
  if (p.kind === "video")   return <VideoCard   p={p} index={index} />;
  return <TextCard p={p} index={index} />;
}

function Projects() {
  const [filter, setFilter] = useState("all");
  const list = PROJECTS.filter((p) =>
    filter === "all" ? true : (p.tags || []).includes(filter)
  );

  return (
    <section className="vsd-projects" id="projects">
      <header className="vsd-projects__head">
        <p className="vsd-projects__eyebrow">
          <span className="vsd-projects__rule" />
          {" "}Selected Work{" "}
          <span className="vsd-projects__rule" />
        </p>
        <h2 className="vsd-projects__title">Projects</h2>
        <p className="vsd-projects__lead">
          From soldering quads in a bedroom to publishing robotics research, building IoT escape rooms, and shipping software inside Daikin heat pumps. Here's the trail of things I've designed, broken, fixed, and (eventually) made work. Pick your flavour below.
        </p>
        <div className="vsd-projects__filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              className={`vsd-projects__filter${filter === f.id ? " is-active" : ""}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </header>

      <div className="vsd-projects__grid">
        {list.map((p) => (
          <ProjectCard key={p.id} p={p} index={PROJECTS.indexOf(p)} />
        ))}
      </div>
    </section>
  );
}

export default Projects;
